import config from "../../../config";
import {
  LOGGER_LOG,
  LOGGER_ERROR,
  LOGGER_LOGS_SEND_PENDING,
  LOGGER_LOGS_SEND_FULFILLED,
  LOGGER_LOGS_SEND_REJECTED,
} from "./constants";
import { AppDispatch, AppGetState } from "../../../store";
import { IBwaIoGateway } from "../../../io/ioGateway";
import { AxiosInstance, AxiosResponse, Method } from "axios";
import { LogRecord } from "./reducer";
import { getGlobalLevelToken } from "../../accessTokenManagerActions";
import { handleApiResponse } from "../../siteContextRequest";

type Params = LogRecord[];

export function log(
  eventId: string,
  eventMetric: string,
  eventMessage: string,
  force?: boolean,
): (dispatch: AppDispatch, getState: AppGetState) => void {
  const logString = `INFO ${eventId} ${eventMetric || "null"} ${eventMessage}`;
  if (config.putLogsToConsole) {
    console.log(logString);
  }
  const payload = createLoggerPayload(logString);
  return function(dispatch: AppDispatch): void {
    dispatch({ type: LOGGER_LOG, payload });
    if (force) {
      sendImmediately(dispatch);
    } else {
      kickSendTimout(dispatch);
    }
  };
}

export function error(
  eventId: string,
  eventMetric: string | null,
  eventMessage: string,
  force?: boolean,
): (dispatch: AppDispatch, getState: AppGetState) => void {
  const logString = `ERROR ${eventId} ${eventMetric || "null"} ${eventMessage}`;
  if (config.putLogsToConsole) {
    console.error(logString);
  }
  const payload = createLoggerPayload(logString);
  return function(dispatch: AppDispatch): void {
    dispatch({ type: LOGGER_ERROR, payload });
    if (force) {
      sendImmediately(dispatch);
    } else {
      kickSendTimout(dispatch);
    }
  };
}

function createLoggerPayload(msg: string) {
  return {
    msg,
    ts: Date.now(),
  };
}

let sendTimeout: NodeJS.Timeout | null = null;

function kickSendTimout(dispatch: AppDispatch) {
  if (sendTimeout) {
    return;
  }
  sendTimeout = setTimeout(() => {
    dispatch(send());
    sendTimeout = null;
  }, config.sendLogsInterval);
}

function sendImmediately(dispatch: AppDispatch) {
  dispatch(send());
  sendTimeout && clearTimeout(sendTimeout);
  sendTimeout = null;
}

export const send = () => (
  dispatch: AppDispatch,
  getState: AppGetState,
  { axios }: IBwaIoGateway,
): unknown => {
  if (!getState().logger.records.length) {
    return;
  }
  dispatch({ type: LOGGER_LOGS_SEND_PENDING });
  return requestWithoutLogging(
    dispatch,
    getState,
    axios,
    "POST",
    config.endpoints.logger,
    getState().logger.sending,
  ).then(
    res => {
      dispatch({ type: LOGGER_LOGS_SEND_FULFILLED });
      return res;
    },
    err => {
      dispatch({ type: LOGGER_LOGS_SEND_REJECTED });
      kickSendTimout(dispatch);
      throw err;
    },
  );
};

// This is fork of request from auth module.
// Can't use original function because we don't want to log
// /logger requests to avoid cyclic calls.
function requestWithoutLogging(
  dispatch: AppDispatch,
  getState: AppGetState,
  axios: AxiosInstance,
  method: Method = "GET",
  url: string,
  params: Params,
): Promise<AxiosResponse> {
  // Can't use signedRequest here because it uses logging itself
  // Logger endpoint doesn't require any permissions so any token should work
  return dispatch(getGlobalLevelToken())
    .then(accessToken => {
      return axios({
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        method,
        url,
        data: params,
      });
    })
    .then(handleApiResponse, handleApiResponse);
}
